import type { ApiOnboardingGraph } from '@onefootprint/request-types/dashboard';
import { EDGE_Z_INDEX } from '../playbook-builder.constants';
import { branchesForNode } from './parse-graph/parse-graph';
import type { InitialEdgeData } from './parse-graph/parse-graph.types';

type CollapseResult = {
  /** Node IDs that should be hidden (not included in dagre, but kept in React Flow state) */
  hiddenIds: Set<string>;
  /** Single bridge edge from collapsed branch → join point */
  bridgeEdges: InitialEdgeData[];
};

const getCollapseResult = (
  edges: InitialEdgeData[],
  graph: ApiOnboardingGraph,
  collapsedNodeIds: string[],
): CollapseResult => {
  const hiddenIds = new Set<string>();
  const bridgeEdges: InitialEdgeData[] = [];

  if (collapsedNodeIds.length === 0) return { hiddenIds, bridgeEdges };

  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    if (!adjacency.has(edge.source)) adjacency.set(edge.source, []);
    adjacency.get(edge.source)?.push(edge.target);
  }

  for (const collapsedId of collapsedNodeIds) {
    const apiNode = graph.nodes.find(n => n.id === collapsedId);
    if (!apiNode || (apiNode.kind !== 'branch' && apiNode.kind !== 'conditional')) continue;

    const joinPointNode = graph.nodes.find(n => n.kind === 'join_point' && n.splitNodeId === collapsedId);
    if (!joinPointNode) continue;

    const queue = [...(adjacency.get(collapsedId) ?? [])];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current === joinPointNode.id || visited.has(current)) continue;
      visited.add(current);
      hiddenIds.add(current);
      for (const neighbor of adjacency.get(current) ?? []) {
        queue.push(neighbor);
      }
    }

    const branchCount = branchesForNode(apiNode).length;

    bridgeEdges.push({
      id: `${collapsedId}-bridge-${joinPointNode.id}`,
      source: collapsedId,
      target: joinPointNode.id,
      type: 'edge',
      zIndex: EDGE_Z_INDEX,
      data: {
        sourceId: collapsedId,
        targetId: joinPointNode.id,
        isTargetLabel: false,
        edgeIndex: 0,
        collapsed: true,
        collapsedLabel: `${branchCount} branches hidden`,
        collapsedSplitNodeId: collapsedId,
      },
    });
  }

  return { hiddenIds, bridgeEdges };
};

export default getCollapseResult;
